import * as React from 'react';

import * as WjCore from '@grapecity/wijmo';
import * as WjInput from '@grapecity/wijmo.input';

import { ApiClient } from './apiClient';
import * as Common from './common';
import * as Const from './consts';

/**
 * JSON データの値を適切なオブジェクトに復元します。
 * @param key キー。
 * @param value 値。
 * @returns 復元された値。
 */
export const reviveFromJson = (key: any, value: any): any => {
  if (typeof value === 'string') {
    const m = value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+\-]\d{2}:\d{2})$/);

    if (m) {
      return new Date(m[0]);
    } else {
      let ret = value; //  置換なしの場合はそのまま返す

      // 1文字ずつチェックする
      for (let index = 0; index < value.length; index++) {
        var targetChar = value.charCodeAt(index);
        const obj = Const.Common.gaijiTanniList.find((element) => element.charCode == targetChar);
        if (obj) {
          // 外字の単位がある場合は変換する
          ret = ret.split(value.charAt(index)).join(obj.convertText);
        }
      }

      return ret;
    }
  }

  return value;
};

/**
 * 改行コード(\n)を改行タグ(<br>)に置換
 * @param text
 */
export function nl2br(text: string) {
  if (text == null || text == undefined || text.length == 0) {
    return text;
  }

  const regex = /(\n)/g;
  return text.split(regex).map(function (line) {
    if (line.match(regex)) {
      return React.createElement('br');
    } else {
      return line;
    }
  });
}

export function nl2rn(text: string): string {
  if (text == null || text == undefined || text.length == 0) {
    return text;
  }

  const regex = /(\n)/g;
  return text.replace(regex, '\r\n');
}

export function nr2nl(text: string | undefined): string | undefined {
  if (text == null || text == undefined || text.length == 0) {
    return text;
  }

  const regex = /(\r?\n)/g;
  return text.replace(regex, '\n');
}

export const isDirBunruiKubun = (bunruiKubun: string) => {
  return Const.DirBunruiKubunList.includes(bunruiKubun);
};

/**
 * ログイン中ユーザの認証・認可状態を取得
 * */
export const getUserInfo = (): Promise<IUserInfo> => {
  return ApiClient.get('/Login/UserInfo');
};

/**
 * ログイン中ユーザの認証・認可状態を取得(外部版)
 * */
export const getUserInfoForCloud = (identifier: string, eno: number, defIdentifier: string): Promise<IUserInfo> => {
  const params = new URLSearchParams({
    identifier: identifier,
    eno: eno.toString(),
    defIdentifier: defIdentifier,
  });
  return ApiClient.get(`/Store/${identifier}/Login/UserInfoForCloud?${params}`);
};

/**
 * 薬剤師一覧
 * */
export const getPharmacist = (
  isPhm?: boolean,
  yzcd?: number,
  usef?: string,
  identifier?: string
): Promise<TPharmasist[]> => {
  let path = isPhm ? '~/Common/Pharmacist' : '/Common/EautPharmacist';
  const params = new URLSearchParams();

  if (window.aerith.isSynthesisEnabled && path.indexOf('~') < 0) {
    path = `/Store/` + identifier + path;
  }

  if (window.aerith.isSynthesisEnabled && isPhm && path.indexOf('~') == 0) {
    path = `/Store/` + identifier + path.replace('~', '');
  }

  if (yzcd !== undefined) {
    params.append('yzcd', yzcd.toString());
  }

  if (isPhm && usef !== undefined) {
    params.append('usef', usef);
  }

  return ApiClient.get(`${path}?${params}`);
};

/**
 * 医療機関検索
 */
export const getMedicalList = (): Promise<TMedicalSearchResult[]> => {
  const params = new URLSearchParams({
    firstFlg: 'true',
    typeId: '0',
    searchStr: '',
    usef: '0',
  });

  return ApiClient.get(`~/Common/SearchIryDialog?${params}`);
};

export const getMedicalDetail = (ircd: number): Promise<unknown> => {
  const params = new URLSearchParams({
    ircd: ircd.toString(),
  });

  return ApiClient.get(`~/Medical/Detail?${params}`);
};

/**
 * 医療機関検索
 */
export const getDepartmentList = (ircd: string): Promise<TDepartmentSearchResult> => {
  const params = new URLSearchParams({
    typeId: '0',
    searchStr: '',
    ircd: ircd,
  });

  return ApiClient.get(`~/Common/MedicalSearchSkaDialog?${params}`);
};

/**
 * 医療機関検索
 */
export const getDoctorList = (ircd: string, kcd: string): Promise<TDoctorSearchResult> => {
  const params = new URLSearchParams({
    typeId: '0',
    searchStr: '',
    ircd: ircd,
    kcd: kcd,
  });

  return ApiClient.get(`~/Common/MedicalSearchIsiDialog?${params}`);
};

/**
 * 医療機関検索
 */
export const getDoctorDetail = (ircd: number, kcd: number, iscd: number): Promise<unknown> => {
  const params = new URLSearchParams({
    ircd: ircd.toString(),
    kcd: kcd.toString(),
    iscd: iscd.toString(),
  });

  return ApiClient.get(`~/Medical/Detail?${params}`);
};

/**
 * 患者検索の取得
 */
export const getSearchResult = (store: string, searchMode: number, searchParams: { [key: string]: string }): Promise<TPSearchResult> => {
  let params = new URLSearchParams(searchParams);
  if (store == '') {
    return ApiClient.get(`~/Patient/Search?${params}`);
  } else {
    return ApiClient.get(`/Store/${store}/Patient/Search?${params}`);
  }
};

/**
 * 患者検索の取得(処方なし患者のみ)
 */
export const getSearchResultNoPrescription = (
  store: string,
  searchParams: { [key: string]: string }
): Promise<TPSearchResult> => {
  const params = new URLSearchParams(searchParams);
  if (store == '') {
    return ApiClient.get(`~/Patient/SearchNoPrescription?${params}`);
  } else {
    return ApiClient.get(`/Store/${store}/Patient/SearchNoPrescription?${params}`);
  }
};

/**
 * 患者検索の取得(処方なし患者含む)
 */
export const getSearchResultAll = (store: string, searchParams: { [key: string]: string }): Promise<TPSearchResult> => {
  const params = new URLSearchParams(searchParams);
  if (store == '') {
    return ApiClient.get(`~/Patient/SearchAll?${params}`);
  } else {
    return ApiClient.get(`/Store/${store}/Patient/SearchAll?${params}`);
  }
};

/**
 * 患者データの取得
 */
export const getPatientDetail = (_kjno: number): Promise<TPatient[]> => {
  const params = new URLSearchParams({
    kjno: _kjno.toString(),
  });

  return ApiClient.get(`~/Patient/Detail?${params}`);
};

/**
 * 日付・受付番号指定で処方歴を取得
 * @param _kjno
 * @param _tymd　指定日付以前
 * @param _mode （ 0：全件　1：直近３調剤日　）
 */
export const getPrescriptionList = (
  _kjno: number,
  _tymd: Date | null,
  _ukno: number | null,
  _mode = 0
): Promise<Shoho> => {
  const params = new URLSearchParams({
    kjno: _kjno.toString(),
    mode: _mode.toString(),
  });

  if (_tymd != null) {
    params.append('tymd', _tymd.toISOString());
  }

  if (_ukno != null) {
    params.append('ukno', _ukno.toString());
  }

  return ApiClient.get(`~/Prescription/List?${params}`);
};

/**
 * 日付・受付番号指定で処方歴を取得
 * @param _kjno
 * @param _tymd　指定日付以前
 * @param _mode （ 0：全件　1：直近３調剤日　）
 */
export const getPrescriptionListWithGuid = (
  _kjno: number,
  _tymd: Date | null,
  _ukno: number | null,
  _mode = 0,
  _guid_kjno: string,
  _identifier: string,
  _csid: number
): Promise<Shoho> => {
  const params = new URLSearchParams({
    kjno: _kjno.toString(),
    mode: _mode.toString(),
    guid_kjno: _guid_kjno.toString(),
    csid: _csid.toString(),
  });

  if (_tymd != null) {
    params.append('tymd', _tymd.toISOString());
  }

  if (_ukno != null) {
    params.append('ukno', _ukno.toString());
  }

  if (_guid_kjno != null) {
    params.append('guid_kjno', _guid_kjno.toString());
  }

  if (_identifier != null) {
    params.append('identifier', _identifier.toString());
  }

  if (_csid != null) {
    params.append('csid', _csid.toString());
  }

  return ApiClient.get(`/Store/${_identifier}/Prescription/List?${params}`);
};

/**
 * 日付・受付番号指定で処方歴を取得
 * @param _kjno
 * @param _tymd　指定日付以前
 * @param _mode （ 0：全件　1：直近３調剤日　）
 */
export const getPrescriptionListWithKflg = (
  _kjno: number,
  _tymd: Date | null,
  _ukno: number | null,
  _kflg: number | null,
  _mode = 0,
  _guid_kjno: string
): Promise<Shoho> => {
  const params = new URLSearchParams({
    kjno: _kjno.toString(),
    mode: _mode.toString(),
  });

  if (_tymd != null) {
    params.append('tymd', _tymd.toISOString());
  }

  if (_ukno != null) {
    params.append('ukno', _ukno.toString());
  }

  if (_kflg != null) {
    params.append('kflg', _kflg.toString());
  }

  return ApiClient.get(`~/Prescription/List?${params}`);
};

export const getPrescriptionCompare = (
  _tymd: Date,
  _ukno: number,
  _tymd2: Date,
  _ukno2: number
): Promise<Compare[]> => {
  const params = new URLSearchParams({
    Tymd: _tymd.toISOString(),
    Ukno: _ukno.toString(),
    Tymd2: _tymd2.toISOString(),
    Ukno2: _ukno2.toString(),
  });

  return ApiClient.get(`~/Prescription/Compare?${params}`);
};

/**
 * 日付・受付番号指定で処方データを取得
 */
export const getPrescriptionDetail = (_tymd: Date, _ukno: number, _kjno: number | null): Promise<unknown> => {
  const params = new URLSearchParams({
    tymd: _tymd.toISOString(),
    ukno: _ukno.toString(),
  });

  if (_kjno != null) {
    params.append('kjno', _kjno.toString());
  }

  return ApiClient.get(`~/Prescription/Detail?${params}`);
};

/**
 * 患者番号指定で指導履歴データを取得
 */
export const getInstructionList = (_kjno: number): Promise<ShidoHeader[]> => {
  const params = new URLSearchParams({
    kjno: _kjno.toString(),
  });

  return ApiClient.get(`~/Instruction/List?${params}`);
};

/**
 * 日付・受付番号指定で指導データを取得
 */
export const getInstructionDetail = (_kjno: number, _tymd: Date, _ukno?: number, _eseq?: number): Promise<Shido> => {
  const params = new URLSearchParams({
    kjno: _kjno.toString(),
    tymd: _tymd.toISOString(),
    ukno: _ukno?.toString() ?? '',
    eseq: _eseq?.toString() ?? '',
  });

  return ApiClient.get(`~/Instruction/Detail?${params}`);
};

/**
 * 訪問予定実績リストデータ(カレンダー用リスト)の取得
 */
export const getVisitSchedule = (_kbn: number): Promise<TSchedule[]> => {
  const params = new URLSearchParams({
    yjkbn: _kbn.toString(),
  });

  return ApiClient.get(`~/VisitSchedule/List?${params}`);
};

/**
 * 訪問予定実績データ（簡易登録画面用）の取得
 */
export const getVisitResult = (kjno: number, zno?: number, zhymd?: Date, yjkbn?: number): Promise<unknown> => {
  const params = new URLSearchParams({
    kjno: kjno.toString(),
    zno: zno?.toString() ?? '0',
    zhymd: (zhymd ?? new Date()).toISOString(),
    yjkbn: yjkbn?.toString() ?? '0',
    hyzcd: '0',
  });

  return ApiClient.get(`~/VisitSchedule/Simple?${params}`);
};

/**
 * 訪問予定実績データ（簡易登録画面用）の登録
 */
export const postVisitResult = (_model: TZPostSimple, _zhymd: Date): Promise<number> => {
  if (_model === null) {
    throw new Error(`${Object.keys({ _model })[0]} must not be NULL.`);
  }

  const data = new URLSearchParams({
    kjno: _model.kjno.toString(),
    zno: _model.zno.toString(),
    y_zhymd: _model.y_zhymd?.toISOString() ?? '',
    sy_stime: _model.y_stime?.toISOString() ?? '',
    sy_etime: _model.y_etime?.toISOString() ?? '',
    y_memo: _model.y_memo ?? '',
    y_hyzcd: _model.y_hyzcd?.toString() ?? '',
    j_zhymd: _model.j_zhymd?.toISOString() ?? '',
    sj_stime: _model.j_stime?.toISOString() ?? '',
    sj_etime: _model.j_etime?.toISOString() ?? '',
    j_memo: _model.j_memo ?? '',
    j_hyzcd: _model.j_hyzcd?.toString() ?? '',
    yjkbn: _model.yjkbn.toString(),
    targetDate: _zhymd.toISOString(),
  });

  return ApiClient.post('~/VisitSchedule/Simple', data);
};

export const getPatientList = (condition: number, searchword: string, page: number): Promise<IJsonResponse> => {
  const params = new URLSearchParams({
    condition: condition.toString(),
    searchword: searchword,
    rows: page.toString(),
  });

  return ApiClient.get(`~/Patient/List?${params}`, { useCommonResultParser: false });
};

export const getPatientListForCloud = (
  condition: number,
  searchword: string,
  page: number,
  identifier: string
): Promise<IJsonResponse> => {
  const params = new URLSearchParams({
    condition: condition.toString(),
    searchword: searchword,
    rows: page.toString(),
  });

  return ApiClient.get(`/Store/${identifier}/Patient/List?${params}`, { useCommonResultParser: false });
};

/**
 * バイタルデータ（パレット用）の登録
 */
export const postVitalResult = (
  kjno: number,
  vitalKbn: number,
  ksaymd: Date,
  vitalData: vitalPushModel[]
): Promise<unknown> => {
  const data = new URLSearchParams({
    kjno: kjno.toString(),
    ksaymd: ksaymd.toISOString(),
  });

  for (let i = 0; i < vitalData.length; i++) {
    if (vitalData[i]) {
      data.append(`[${i}].vtkbn`, vitalData[i].vtkbn.toString());
      data.append(`[${i}].vtcode`, vitalData[i].vtcode.toString());
      data.append(`[${i}].vtname`, vitalData[i].vtname);
      data.append(`[${i}].vtvalue`, vitalData[i].vtvalue);
    }
  }

  return ApiClient.post('~/Patient/UpdateVitalInfo', data);
};

/**
 * 外字単位の変換
 */
export function convertGaijiTanni(text: string): string {
  let ret = text; //  置換なしの場合はそのまま返す

  // 1文字ずつチェックする
  for (let index = 0; index < text.length; index++) {
    var targetChar = text.charCodeAt(index);
    const obj = Const.Common.gaijiTanniList.find((element) => element.charCode == targetChar);
    if (obj) {
      // 外字の単位がある場合は変換する
      ret = ret.split(text.charAt(index)).join(obj.convertText);
    }
  }

  return ret;
}

/**
 * 薬剤師リストからインデックスを取得する
 */
export function getListIndex(_list: Array<TPharmasist>, search: number): number {
  let _return = 0;
  if (!WjCore.isUndefined(_list) && _list.length > 0 && !WjCore.isUndefined(search) && search != null) {
    _list.forEach((val, index) => {
      if (Number(val.yzcd) == Number(search)) {
        _return = index;
      }
    });
  }
  return _return;
}

/**
 * 日付コンポーネントのフォーマットの初期化
 */
export function onInputDateFormatInitialized(t: WjInput.InputDate) {
  t.calendar.formatYearMonth = 'gyy年M月';
  t.calendar.formatYear = 'gggyy年';
}

/**
 * JCTの登録
 */
export const insertJct = (kjno: number, gyonm: string): Promise<string> => {
  const data = new URLSearchParams({
    kjno: kjno.toString(),
    gyonm: gyonm,
  });

  return ApiClient.post('~/Common/InsertJctKjno', data);
};

/**
 * JCTの更新
 */
export const updateJct = (kjno: number, gyonm: string): Promise<boolean> => {
  const data = new URLSearchParams({
    kjno: kjno.toString(),
    gyonm: gyonm,
  });

  return ApiClient.post('~/Common/UpdateJctKjno', data);
};

/**
 * JCTの削除
 */
export const deleteJct = (kjno: number): Promise<boolean> => {
  const params = new URLSearchParams({
    kjno: kjno.toString(),
  });

  return ApiClient.post('~/Common/DeleteJctKjno', params);
};

/**
 * JCTの削除　※ページ遷移イベント用
 */
export const deleteJctOnUnload = (kjno: number) => {
  const model = new URLSearchParams();
  model.append('kjno', kjno.toString());
  const url = Common.getFullPathName('~/Common/DeleteJctKjno');
  return navigator.sendBeacon(url, model);
};

/**
 * JCTの作成
 */
export const createJct = async (kjno: number, gyonm: string): Promise<boolean> => {
  const message: string = await insertJct(kjno, gyonm);
  if (message !== '') {
    if (!confirm(message)) {
      return false;
    } else {
      // JCTの上書きを行う
      if (!(await updateJct(kjno, gyonm))) {
        return false;
      }
    }
  }
  return true;
};

/**
 * ページ遷移時イベント（ページ遷移前）
 * ※ios safariでは動作しない
 */
export const handleBeforeUnload = (e: any, isViewMessage: boolean) => {
  if (isViewMessage) {
    // ページ遷移前に確認メッセージを表示
    e.preventDefault();
    // ※ChromeではreturnValueを設定する必要がある
    // 画面に表示したいメッセージを設定（Chromeはそれを無視してブラウザ定義のメッセージを表示）
    e.returnValue = '画面を終了します。よろしいですか？';
  }
};

/**
 * ページ遷移時イベント（ページ遷移後）
 * ※ios safari用
 */
export const handlePageHide = (e: any, kjno: number) => {
  // JCTの削除
  deleteJctOnUnload(kjno);
};

/**
 * ページ遷移時イベント（ページ遷移後）
 * ※ios safariでは動作しない
 */
export const handleUnload = (e: any, kjno: number) => {
  // JCTの削除
  deleteJctOnUnload(kjno);
};

/**
 * エアリスライセンスチェック
 */
export const getLicenseAerith = (): Promise<boolean> => {
  return ApiClient.get(`~/Common/CheckLicenseAerith`);
};

/**
 * 服薬指導ライセンスチェック
 */
export const getLicenseSido = (): Promise<boolean> => {
  return ApiClient.get(`~/Common/CheckLicenseSido`);
};

/**
 * ＠HOMEライセンスチェック
 */
export const getLicenseAtHome = (): Promise<boolean> => {
  return ApiClient.get(`~/Common/CheckLicenseAtHome`);
};

/**
 * 服薬システム管理設定取得
 * syscdはESYSテーブルのSYSCD(MONITOR_TEL_FLG)などを指定すること。
 */
export const GetEsys = (syscd: string, _identifier: string): Promise<string> => {
  const data = new URLSearchParams({
    syscd: syscd,
  });
  if (_identifier != null && _identifier != '') {
    return ApiClient.post(`/Store/${_identifier}/Common/GetEsys`, data);
  } else {
    return ApiClient.post('~/Common/GetEsys', data);
  } 
};

/**
 * システム管理設定取得
 */
export const GetSys = (kijunymd: Date): Promise<TSYS> => {
  const data = new URLSearchParams({
    kijunymd: kijunymd.toISOString(),
  });

  return ApiClient.post('~/Common/GetSys', data);
};

/**
 * ZYJの更新（処方）
 */
export const updateZyjSho = (kjno: number, zno: number, tymd: Date, ukno: number): Promise<void> => {
  const data = new URLSearchParams({
    kjno: kjno.toString(),
    zno: zno.toString(),
    tymd: tymd.toISOString(),
    ukno: ukno.toString(),
  });

  return ApiClient.post('~/VisitSchedule/UpdateZyjSho', data);
};

/**
 * 年齢算出
 * @param bymd
 * @param baseDate
 */
export const calcAge = (bymd: Date, baseDate: Date = new Date()): string => {
  let y = baseDate.getFullYear() - bymd.getFullYear();
  let m = baseDate.getMonth() - bymd.getMonth();
  let d = baseDate.getDate() - bymd.getDate();

  if (d < 0) {
    m--;
  }

  if (m < 0) {
    y--;
    m += 12;
  }

  return String(y) + '歳' + String(m) + 'ヶ月';
};

/**
 * 指導画像一覧取得
 * @param kjno
 * @param eseq
 * @param tymd
 * @param ukno
 */
export const getInstructionImages = (
  kjno?: number,
  eseq?: number,
  tymd?: Date,
  ukno?: number,
  uknoList?: number[]
): Promise<TSidoImageResult> => {
  console.log('getInstructionImages');
  const params = new URLSearchParams({
    kjno: kjno ? kjno.toString() : '',
    eseq: eseq ? eseq.toString() : '',
    tymd: tymd ? tymd.toISOString() : '',
    ukno: ukno ? ukno.toString() : '',
  });
  uknoList?.forEach((item) => params.append('uknoList', item.toString()));

  return ApiClient.get(`~/Instruction/Images?${params}`);
};

/**
 * デフォルト店舗取得
 */
export const getDefaultStore = (): any => {
  return ApiClient.get(`/Instruction/GetDefaultStore`);
};

/**
 * 参照可能店舗取得
 */
export const getPossibleStore = (): any => {
  return ApiClient.get(`/User/ParmittedStore`);
};

/**
 * 店舗情報取得
 */
export const getStoreInfo = (): any => {
  return ApiClient.get(`~/Instruction/GetStoreInfo`);
};

/**
 * 店舗eno取得
 */
export const getStoreEno = (csid: number, eno: number): Promise<ChainEautAuth> => {
  const params = new URLSearchParams({
    csid: csid.toString(),
    eno: eno.toString(),
  });
  return ApiClient.get<ChainEautAuth>(`/User/GetStoreEno?${params}`);
};

/**
 * ユーザー取得
 */
export const getUser = (eno: number): any => {
  const params = new URLSearchParams({
    eno: eno.toString(),
  });
  return ApiClient.get(`~/User/UserData?${params}`);
};

/**
 * ENAMデータ取得
 */
export const getEnamData = (nam_no: number): Promise<string> => {
  console.log('getEnamData', nam_no);
  const params = new URLSearchParams({
    namno: nam_no.toString(),
  });
  return ApiClient.post(`~/Common/GetEnam`, params);
};

/*
/**
 * 過去指導文を取得する
 */
export const SelectGuidPrevList = (
  _kjno: number,
  _tymd: Date,
  _ukno: number | null,
  _eno: number,
  _identifier: string
): Promise<SidobunSet[]> => {
  const params = new URLSearchParams({
    kjno: _kjno.toString(),
    eno: _eno.toString(),
  });

  if (_ukno != null) {
    params.append('ukno', _ukno.toString());
  }

  if (_eno != null) {
    params.append('eno', _eno.toString());
  }

  if (_identifier != null) {
    params.append('identifier', _identifier.toString());
  }

  if (_identifier != null && _identifier != '') {
    return ApiClient.get(`/Store/${_identifier}/Common/SelectGuidPrevListCloud?${params}`);
  } else {
    return ApiClient.get(`/Common/SelectGuidPrevListCloud?${params}`);
  }
};

/**
 * 名寄せ状態を取得する
 */
export const getPNayose = (_kjno: number, _identifier: string): Promise<TPnayoseType[]> => {
  const params = new URLSearchParams({
    kjno: _kjno.toString(),
  });

  if (_identifier != null && _identifier != '') {
    return ApiClient.get(`/Store/${_identifier}/Instruction/GetPNayose?${params}`);
  } else {
    return ApiClient.get(`~/Instruction/GetPNayose?${params}`);
  }
};

/**
 * Pharms連携設定取得
 */
export const getPharmsSetting = (_identifier: string): any => {
  if (_identifier != null && _identifier != '') {
    return ApiClient.get(`/Store/${_identifier}/Patient/GetPharmsSetting`);
  } else {
    return ApiClient.get('~/Patient/GetPharmsSetting');
  }
};

/**
 * Pharms連携関連設定全取得
 */
export const getPharmsSettingAll = (_identifier: string): any => {
  const params = new URLSearchParams({
    identifier: _identifier.toString(),
  });

  if (_identifier != null && _identifier != '') {
    return ApiClient.get(`/Store/${_identifier}/Patient/GetPharmsSettingAll?${params}`);
  } else {
    return ApiClient.get('~/Patient/GetPharmsSettingAll?${params}');
  }
};

/**
 * Pharms連携関連設定の更新
 */
export const updatePharmsSettings = (
  _enabled: string,
  _url: string,
  _apiKey: string,
  _pharmacyId: string
): Promise<void> => {
  const params = new URLSearchParams({
    enabled: _enabled.toString(),
    url: _url.toString(),
    apiKey: _apiKey.toString(),
    pharmacyId: _pharmacyId.toString(),
  });
  return ApiClient.post(`~/Patient/UpdatePharmsSettings`, params);
};

/**
 * Pharms連携関連設定の削除
 */
export const deletePharmsSettings = (_identifier: string): Promise<void> => {
  if (_identifier != null && _identifier != '') {
    return ApiClient.post(`/Store/${_identifier}/Patient/DeletePharmsSettings`, '');
  } else {
    return ApiClient.post(`~/Patient/DeletePharmsSettings`, '');
  }
};

/**
 * 指導画像種別取得
 */
export const getImageList = (_identifier: string): any => {
  if (_identifier != null && _identifier != '') {
    return ApiClient.get(`/Store/${_identifier}/Patient/getImageList`);
  } else {
    return ApiClient.get('~/Patient/getImageList');
  }
};

/**
 * 詳細検索設定取得
 */
export const getSearchSettings = (_identifier: string): any => {
  if (_identifier != null && _identifier != '') {
    return ApiClient.get(`/Store/${_identifier}/Patient/GetSearchSettings`);
  } else {
    return ApiClient.get('~/Patient/GetSearchSettings');
  }
};

/**
 * 詳細検索設定の登録
 */
export const updateSearchSettings = (
  _identifier: string,
  _namSeq: string,
  _namColumn: string,
  _namValue: string,
  _namDefault: string,
): Promise<void> => {
  const params = new URLSearchParams({
    namSeq: _namSeq,
    namColumn: _namColumn,
    namValue: _namValue,
    namDefault: _namDefault,
  });

  if (_identifier != null && _identifier != '') {
    return ApiClient.post(`/Store/${_identifier}/Patient/UpdateSearchSetting`, params);
  } else {
    return ApiClient.post(`~/Patient/UpdateSearchSetting`, params);
  }

};

/**
 * 指導文入力チェック設定取得
 */
export const getSettings = (_identifier: string): any => {
  if (_identifier != null && _identifier != '') {
    return ApiClient.get(`/Store/${_identifier}/InstructionCategory/GetSettings`);
  } else {
    return ApiClient.get('~/InstructionCategory/GetSettings');
  }
};

/**
 * medimo連携設定の取得
 */
export const getMedimoSetting = (_identifier: string): any => {
  if (_identifier != null && _identifier != '') {
    return ApiClient.get(`/Store/${_identifier}/Patient/GetMedimoSetting`);
  } else {
    return ApiClient.get('~/Patient/GetMedimoSetting');
  }
};
